import { GridColDef } from "@material-ui/data-grid";
import { ReactComponent as DownloadIcon } from "assets/icon/download-icon.svg";
import { ReactComponent as RedTrashIcon } from "assets/icon/red_trash.svg";
import { ReactComponent as ViewIcon } from "assets/icon/view-icon.svg";
import {
  Box,
  IconButton,
  Modal,
  Notification,
  Table,
  Tooltip,
  Typography,
} from "components";
import { filterOutAllOption } from "constants/objects/claim_request_mapping_status";
import { compose, withAuthorize, withHooks, withTranslation } from "enhancers";
import { gql, useLazyQuery } from "hooks";
import { isEmpty, some } from "lodash";
import paths from "routes/paths";
import { AppColor } from "theme/app-color";
import { forceDownload, formatDateWithBuddhistYear } from "utils/helper";
import { hospitalImportListFilterInitialValues } from "./HospitalImportFilters";
import { EnumHospitalImportStatus } from "constants/enums/hospital_import_status";
import Authorize from "components/Authorize";
import { PERMISSIONS } from "constants/enums/permissions";

const HospitalImportTableComponent = (props: any) => (
  <Table
    columns={props.columns}
    rows={props.tableData}
    loading={props.loading}
    density="compact"
    autoHeight
    disableSelectionOnClick
  />
);

export const HOSPITAL_IMPORT_TABLE_API = {
  HOSPITAL_IMPORT_LIST: gql`
    query HOSPITAL_IMPORT_LIST(
      $name: String
      $status: String
      $startDate: String
      $endDate: String
    ) {
      hospitalImportList(
        input: {
          name: $name
          status: $status
          startDate: $startDate
          endDate: $endDate
        }
      ) {
        id
        name
        status
        importDate
        totalRecords
        fileUrl
      }
    }
  `,
  DELETE_HOSPITAL_IMPORT: gql`
    mutation DELETE_HOSPITAL_IMPORT($id: String!) {
      deleteHospitalImport(id: $id)
    }
  `,
};

const enhancer = compose(
  withAuthorize(),
  withTranslation({
    prefix: "pages.main.hospitalImport.table",
  }),
  withHooks((props: any, hooks: any) => {
    const { t, hasPermission } = props;
    const { useMemo, useCallback, useEffect, useUrlParam, useMutation } = hooks;

    const queryParams = useUrlParam();

    const [fetchList, { data, loading, error }] = useLazyQuery(
      HOSPITAL_IMPORT_TABLE_API.HOSPITAL_IMPORT_LIST,
      {
        fetchPolicy: "network-only",
      }
    );

    const [deleteHospitalImport] = useMutation(
      HOSPITAL_IMPORT_TABLE_API.DELETE_HOSPITAL_IMPORT,
      {
        refetchQueries: [
          { query: HOSPITAL_IMPORT_TABLE_API.HOSPITAL_IMPORT_LIST },
        ],
        awaitRefetchQueries: true,
        onCompleted: () => {
          Notification.notify("ทำรายการสำเร็จ");
        },
      }
    );

    const tableData = useMemo(() => {
      return (
        data?.hospitalImportList?.map((item: any) => ({
          ...item,
          importDate: formatDateWithBuddhistYear(
            new Date(item.importDate),
            "dd/MM/yyyy"
          ),
        })) || []
      );
    }, [data?.hospitalImportList]);

    useEffect(() => {
      let count = 1;
      const intervalID = setInterval(() => {
        console.log(count++, "polling hospital import");

        const hasImportingStatus = some(
          data?.hospitalImportList,
          (item: any) => item.status === EnumHospitalImportStatus.IMPORTING
        );

        if (!hasImportingStatus) {
          clearInterval(intervalID);
          return;
        }

        const params = isEmpty(queryParams) ? undefined : queryParams;
        fetchList({ variables: filterOutAllOption(params) });
      }, 5000);

      return () => {
        clearInterval(intervalID);
      };
    }, [data?.hospitalImportList, fetchList, queryParams]);

    const handleConfirmDeleteHospitalImport = useCallback(
      async (id: string) => {
        try {
          await deleteHospitalImport({ variables: { id } });
        } catch (error) {
          console.log(error);
        }
      },
      [deleteHospitalImport]
    );

    const handleClickDeleteRow = useCallback(
      async ({ row }: any) => {
        Modal.open({
          title: t(".confirmDeleteModal.removeHospitalImport"),
          children: (
            <Box display="flex" flexDirection="column">
              <div style={{ marginBottom: "16px" }}>
                <Typography variant="Body/16">
                  {t(".confirmDeleteModal.info1")}
                </Typography>
                <Typography
                  variant="Subtitle/16"
                  color={AppColor["Primary/Primary"]}
                >
                  {` "${row.name}" `}
                </Typography>
                <Typography variant="Body/16">
                  {t(".confirmDeleteModal.info2")}
                </Typography>
              </div>
              <Typography variant="Helper/12" color={AppColor["Other/Warning"]}>
                {t(".confirmDeleteModal.warningInfo")}
              </Typography>
            </Box>
          ),
          cancelButtonLabel: t(".confirmDeleteModal.cancelDelete"),
          okButtonLabel: t(".confirmDeleteModal.confirmDelete"),
          okButtonVariant: "contained",
          okButtonColor: AppColor["SecondaryText/Danger"],
          cancelButtonVariant: "outlined",
          onOk: async ({ close }: any) => {
            close();
            handleConfirmDeleteHospitalImport(row.id);
          },
        });
      },
      [handleConfirmDeleteHospitalImport, t]
    );

    const handleClickToSeeDetail = useCallback((row: any) => {
      paths.hospitalImportDetailPath(row.id).push();
    }, []);

    const downloadFile = useCallback(
      async (excelFileUrl: string, fileName: string) => {
        if (fileName && excelFileUrl) {
          const loadFile = async () => {
            try {
              const blob = await fetch(`${excelFileUrl}`).then((r) => r.blob());
              const file = new File([blob], fileName, {
                type:
                  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
              });
              if (file) {
                const url = URL.createObjectURL(file);
                forceDownload(url, fileName);
              }
              Notification.success(t(".downloadSuccess"));
            } catch (e) {
              Notification.error(e);
            }
          };

          loadFile();
        } else {
          alert("Coming Soon!");
        }
      },
      [t]
    );

    const handleClickDownload = useCallback(
      async (row: any) => {
        await downloadFile(row.fileUrl, row.name);
      },
      [downloadFile]
    );

    const renderActionsCell = useCallback(
      ({ row }: any) => (
        <Box display="flex" style={{ gap: "12px" }}>
          <Tooltip title={t(".download")}>
            <IconButton size="small" onClick={() => handleClickDownload(row)}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title={t(".viewDetail")}>
            <IconButton
              size="small"
              onClick={() => handleClickToSeeDetail(row)}
            >
              <ViewIcon />
            </IconButton>
          </Tooltip>

          <Authorize permissions={[PERMISSIONS.HOSPITAL_IMPORT_EDIT]}>
            <Tooltip title={t(".delete")}>
              <IconButton
                size="small"
                disabled={[
                  EnumHospitalImportStatus.PROCESSED,
                  EnumHospitalImportStatus.IMPORTING,
                ].includes(row.status)}
                onClick={() => handleClickDeleteRow({ row })}
              >
                <RedTrashIcon
                  style={{
                    opacity: [
                      EnumHospitalImportStatus.PROCESSED,
                      EnumHospitalImportStatus.IMPORTING,
                    ].includes(row.status)
                      ? 0.5
                      : 1,
                  }}
                />
              </IconButton>
            </Tooltip>
          </Authorize>
        </Box>
      ),
      [t, handleClickDownload, handleClickToSeeDetail, handleClickDeleteRow]
    );

    const columns = useMemo(
      (): GridColDef[] => [
        {
          width: 400,
          field: "name",
          headerName: t(".columns.name"),
          sortable: true,
        },
        {
          width: 140,
          field: "totalRecords",
          headerName: t(".columns.totalRecords"),
          sortable: true,
          type: "number",
        },
        {
          width: 140,
          field: "status",
          headerName: t(".columns.status"),
          sortable: true,
          renderCell: ({ value }: any) => (
            <Typography variant="Body/16">
              {t(`client.enum.hospitalImportStatus.${value}`)}
            </Typography>
          ),
        },
        {
          width: 200,
          field: "importDate",
          headerName: t(".columns.importDate"),
          sortable: true,
        },
        {
          width: 150,
          field: "actions",
          headerName: "Action",
          filterable: false,
          sortable: false,
          type: "actions",
          renderCell: renderActionsCell,
        },
      ],
      [t, renderActionsCell]
    );

    useEffect(() => {
      const params = isEmpty(queryParams) ? undefined : queryParams;
      const allowedKeys = Object.keys(hospitalImportListFilterInitialValues);
      const hasInvalidKey =
        params && Object.keys(params).some((key) => !allowedKeys.includes(key));
      if (hasInvalidKey) paths.hospitalImportPath().push();
      else {
        fetchList({ variables: filterOutAllOption(params) });
      }
    }, [queryParams, fetchList]);

    return { t, columns, tableData, loading, handleClickToSeeDetail };
  })
);

export default enhancer(HospitalImportTableComponent);
